import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { Loader2, LucideIcon, UploadIcon, UserIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { MediaInfo } from "./types";
import { useUpload } from './useUpload';
import { useImageResize } from './useImageResize';
import { formatFileSize } from './utils';

interface ProfilePhotoUploaderProps {
  formId: string;
  value?: MediaInfo | null;
  onChange: (media: MediaInfo | undefined) => void;
  className?: string;
  disabled?: boolean;
  maxSize?: number;
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  aspectRatio?: number;

  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | number;
  rounded?: boolean;
  fallbackIcon?: LucideIcon;
  onUploadStateChange?: (isUploading: boolean) => void;
}

const sizeMap = {
  sm: 'w-16 h-16',
  md: 'w-24 h-24',
  lg: 'w-32 h-32',
  xl: 'w-40 h-40',
  '2xl': 'w-48 h-48'
};

export function ProfilePhotoUploader({
  value,
  onChange,
  className,
  formId,
  disabled = false,
  maxSize = 5000000,
  maxWidth = 400,
  maxHeight = 400,
  quality = 0.8,
  aspectRatio = 1,
  size = 'md',
  rounded = true,
  onUploadStateChange,
  fallbackIcon: FallbackIcon = UserIcon
}: ProfilePhotoUploaderProps) {
  const { uploads, hasActiveUploads, handleUpload } = useUpload(formId);
  const [localPreview, setLocalPreview] = useState<string>();

  // Validate the incoming value - treat as null if it's truthy but lacks an ID
  const validatedValue = value && typeof value.id === 'number' ? value : null;

  const { resizeImage, isProcessing } = useImageResize({
    maxWidth,
    maxHeight,
    aspectRatio: aspectRatio, // Force square aspect ratio for profile photos
    quality,
    mimeType: 'image/jpeg'
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: async (acceptedFiles, rejectedFiles) => {
      if (disabled) return;

      rejectedFiles.forEach(({ file, errors }) => {
        errors.forEach(error => {
          if (error.code === 'file-too-large') {
            toast.error(`Image is too large. Maximum size is ${formatFileSize(maxSize)}`);
          } else if (error.code === 'file-invalid-type') {
            toast.error('Only JPEG, PNG and WebP images are allowed');
          }
        });
      });

      if (acceptedFiles.length > 0) {
        try {

          // Create and set local preview immediately
          const localPreviewUrl = URL.createObjectURL(acceptedFiles[0]);
          setLocalPreview(localPreviewUrl);

          const result = await resizeImage(acceptedFiles[0]);
          handleUpload(result.file);

        } catch (err) {
          toast.error('Error processing image');
          console.error('Error resizing image:', err);
        }
      }
    },
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.webp']
    },
    maxSize,
    maxFiles: 1,
    multiple: false,
    disabled,
    noClick: disabled
  });

  useEffect(() => {
    // Find the last completed upload with mediaInfo
    const lastCompletedUpload = [...uploads]
      .reverse()
      .find(upload => upload?.status === 'completed' && upload.mediaInfo);

    if (lastCompletedUpload) {
      onChange(lastCompletedUpload.mediaInfo);
    }
    // Only trigger onChange when uploads array changes, specifically when a new upload completes
  }, [uploads, onChange]);

  useEffect(() => {
    return () => {
      if (localPreview) {
        URL.revokeObjectURL(localPreview);
      }
    };
  }, [localPreview]);

  const isUploading = hasActiveUploads || isProcessing;
  const uploadProgress = uploads[0]?.progress || 0;

  // Effect to notify parent about upload state changes
  useEffect(() => {
    onUploadStateChange?.(isUploading);
  }, [isUploading, onUploadStateChange]);

  const displayImage = localPreview || validatedValue?.preview_url;

  const sizeClass = typeof size === 'number'
    ? `w-[${size}px] h-[${size}px]`
    : sizeMap[size];


  const getIconSize = () => {
    const sizes = {
      sm: 'w-8 h-8',
      md: 'w-12 h-12',
      lg: 'w-16 h-16',
      xl: 'w-20 h-20',
      '2xl': 'w-24 h-24'
    };
    return typeof size === 'number'
      ? `w-[${size / 2}px] h-[${size / 2}px]`
      : sizes[size];
  };


  return (
    <div
      {...getRootProps()}
      className={cn(
        "relative group cursor-pointer",
        disabled && "cursor-not-allowed opacity-60",
        sizeClass,
        className
      )}
    >
      <input {...getInputProps()} />
      <Avatar className={cn(
        sizeClass,
        "border-2 border-dashed border-gray-200",
        isDragActive && "border-primary",
        "transition-all duration-200",
        "hover:border-primary hover:opacity-90",
        rounded ? "rounded-full" : "rounded-md"
      )}>
        <AvatarImage
          src={displayImage}
          className={cn(
            rounded ? "rounded-full" : "rounded-md",
            "object-cover"
          )}
        />
        <AvatarFallback className={cn(
          rounded ? "rounded-full" : "rounded-md"
        )}
        >
          {isUploading ? (
            <div className="flex flex-col items-center justify-center">
              <Loader2 className={cn(
                getIconSize(),
                "animate-spin"
              )} />
              <span className="text-xs mt-1">
                {isProcessing ? 'Processing...' : `${uploadProgress}%`}
              </span>
            </div>
          ) : (
            <FallbackIcon className={cn(
              getIconSize(),
              "text-gray-400"
            )} />
          )}
        </AvatarFallback>
      </Avatar>

      <div className={cn(
        "absolute inset-0 flex items-center justify-center",
        "bg-black/60 opacity-0",
        rounded ? "rounded-full" : "rounded-md",
        "group-hover:opacity-100 transition-opacity",
        !isUploading && "hidden"
      )}>
        <UploadIcon className={getIconSize()} />
      </div>
    </div>
  );
}
